/**
 * PieChart — DGUI component for pie/donut (composition, share of whole).
 */

import { Cell, Legend, Pie, PieChart as RechartsPieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, Typography } from '@mui/material';
import type { PieChartDGUIProps } from '@flowrithm/shared/src/features/ai-assist/types/dgui';

const cardSx = {
  borderRadius: 2,
  borderColor: 'rgba(4, 55, 123, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  mt: 1,
  mb: 1,
  overflow: 'hidden',
};

const DEFAULT_COLORS = ['#04377b', '#5c8fd4', '#8fb8e8', '#b8d4f0', '#7b9acc', '#4a7ab8'];

function formatValue(
  value: number,
  format?: 'number' | 'percent' | 'currency',
  currencyCode?: string
): string {
  if (format === 'percent') return `${value}%`;
  if (format === 'currency' && currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return `${currencyCode} ${value}`;
    }
  }
  return String(value);
}

function normalizePieProps(props: PieChartDGUIProps & Record<string, unknown>): PieChartDGUIProps {
  return {
    ...props,
    showLabels: props.showLabels ?? (props.show_labels as boolean),
    labelFormat: (props.labelFormat ?? props.label_format) as PieChartDGUIProps['labelFormat'],
    valueFormat: (props.valueFormat ?? props.value_format) as PieChartDGUIProps['valueFormat'],
    currencyCode: props.currencyCode ?? (props.currency_code as string),
  };
}

export function PieChart(initialProps: PieChartDGUIProps & Record<string, unknown>) {
  const {
    title,
    subtitle,
    segments = [],
    variant = 'pie',
    showLabels = true,
    labelFormat = 'percent',
    colors = DEFAULT_COLORS,
    valueFormat = 'number',
    currencyCode,
  } = normalizePieProps(initialProps);
  const safeSegments = Array.isArray(segments) ? segments : [];
  const total = safeSegments.reduce((sum, s) => sum + (s.value ?? 0), 0);
  const chartData = safeSegments.map((s, i) => ({
    name: s.label,
    value: s.value ?? 0,
    percent: total > 0 ? ((s.value ?? 0) / total) * 100 : 0,
    color: colors[i % colors.length],
  }));
  const isEmpty = chartData.length === 0 || total === 0;

  const renderLabel = (entry: { name: string; value: number; percent: number }) => {
    if (!showLabels) return null;
    if (labelFormat === 'percent') return `${entry.percent.toFixed(1)}%`;
    return formatValue(entry.value, valueFormat, currencyCode);
  };

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {title && (
          <Typography variant="subtitle1" fontWeight={600} color="#04377b" gutterBottom>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary">
            No data
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <RechartsPieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={variant === 'donut' ? '55%' : 0}
                outerRadius="80%"
                label={renderLabel}
                labelLine={showLabels}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string, item: { payload?: { percent?: number } }) => [
                  labelFormat === 'percent'
                    ? `${(item.payload?.percent ?? 0).toFixed(1)}%`
                    : formatValue(value, valueFormat, currencyCode),
                  name,
                ]}
              />
              <Legend />
            </RechartsPieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
